import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FormPageLayout } from '../../../../../components/layout/FormPageLayout';
import { Button } from '../../../../../components/ui/Button';
import { Input, FormField } from '../../../../../components/ui/FormField';
import { useToast } from '../../../../../components/ui/Toast';
import { Money } from '../../../../../components/ui/Money';
import { Decimal } from 'decimal.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../../lib/locale';
import { useWriteOffs, useRecordWriteOffRecovery } from '../../../../../features/finance/hooks';
import { usePageTitle } from '../../../../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Banner } from '../../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/finance/write-offs/$id/recover')({
  component: RecoverDebtPage,
});

interface WriteOffRecord {
  id: string;
  subscriberName: string | null;
  ticketNumber: number | null;
  schemeCode: string | null;
  amountPaise: number;
  recoveredPaise: number;
  writeOffDate: string;
  status: 'WRITTEN_OFF' | 'RECOVERING' | 'RECOVERED';
}

const recoverFormSchema = z.object({
  rupeesRecovered: z.string().min(1, 'Amount is required'),
});

type FormValues = z.infer<typeof recoverFormSchema>;

const recoverFormConfig = defineFormPage<FormValues>({
  resource: 'write-offs',
  defaultValues: {
    rupeesRecovered: '',
  },
  schema: recoverFormSchema,
});

function RecoverDebtPage() {
  usePageTitle('Recover Debt');
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { toast } = useToast();
  const { data: response } = useWriteOffs();
  const records = (response?.data ?? []) as WriteOffRecord[];
  const selectedRecord = records.find(r => r.id === id);
  const recoverMutation = useRecordWriteOffRecovery();

  const {
    form,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(recoverFormConfig, recoverMutation, {
    routeId: `finance/write-offs/${id}/recover`,
    onBeforeSubmit: (values) => {
      let recovered: Decimal;
      try {
        recovered = new Decimal(values.rupeesRecovered);
      } catch {
        toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount.' });
        throw new Error('Invalid amount');
      }
      if (recovered.isNaN() || recovered.lte(0)) {
        toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount.' });
        throw new Error('Invalid amount');
      }
      if (!selectedRecord) throw new Error('No record selected');
      return { id: selectedRecord.id, amountPaise: recovered.mul(100).round().toNumber() };
    },
  });

  const onFormSubmit = async (e: React.FormEvent) => {
    try {
      await handleSubmit(e);
      clearDraft();
      toast({
        type: 'success',
        title: 'Recovery Logged',
        description: `Successfully logged recovery. Assets credited in cash box.`,
      });
      void navigate({ to: '/finance/write-offs' });
    } catch (err) {
      console.error('Failed to log write-off recovery', err);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Failed to log recovery. Please try again.',
      });
    }
  };

  return (
    <FormPageLayout
      title="Log Bad Debt Recovery Payment"
      subtitle="Record cash/assets recovered from previously written-off provisions."
      backTo="/finance/write-offs"
      variant="card"
    >
      <form onSubmit={onFormSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            {selectedRecord ? (
              <div className="bg-surface-2 p-3.5 rounded-lg border border-border flex flex-col gap-1 mb-4">
                <p className="text-xs text-text-muted font-bold">Bad Debt Account</p>
                <p className="text-sm font-bold text-text-primary">
                  {selectedRecord.subscriberName ?? 'Unlinked'} {selectedRecord.ticketNumber ? `(Ticket ${selectedRecord.ticketNumber})` : ''}
                </p>
                <p className="text-xs font-semibold text-danger-500 mt-1 flex items-center gap-1">
                  Outstanding Loss: <Money paise={selectedRecord.amountPaise - selectedRecord.recoveredPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </p>
              </div>
            ) : (
              <div className="bg-danger-bg/10 border border-danger-bg p-3 rounded-lg text-xs text-danger-text">
                Loading bad debt account...
              </div>
            )}

            <form.Field
              name="rupeesRecovered"
              children={(field: any) => (
                <FormField
                  label="Recovered Payment Received (₹)"
                  required
                  error={fe(field.state.meta)}
                >
                  <Input
                    type="number"
                    placeholder="e.g. 25000"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={!selectedRecord || recoverMutation.isPending}
                  />
                </FormField>
              )}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="secondary"
                onClick={() => void navigate({ to: '/finance/write-offs' })}
                disabled={recoverMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={recoverMutation.isPending}
                disabled={!selectedRecord}
              >
                Save Recovery
              </Button>
            </div>
          </form>
    </FormPageLayout>
  );
}
